import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { clearInterval, clearTimeout, setInterval, setTimeout } from 'worker-timers';
import { ButtonV2, Modal, actions as MessageBoxActions } from '@vibrent/electryon';
import EventListener from 'react-event-listener';
import { bindActionCreators } from 'redux';
import { ensurePromiseWithResolvers } from '../../../../config/promiseWithResolversPolyfill';
import { updateJwtToken } from '../../actions/userActions';
import { Constants, SESSION_EXTENDED_SUCCESS_MESSAGE } from '../common/constants';
import * as actions from './actions';
import styles from './styles.scss';
import dispatchEndCatiSession from '../../utils/endCatiSession';

const {
  InformationModal,
} = Modal;

/**
 * Refresher
 * @extends Component
 */
class SessionRefresher extends Component {
  /**
   * Constructor
   * @param {Object} props
   */
  constructor(props) {
    super(props);
    this.refreshSession = this.refreshSession.bind(this);
    setInterval(this.refreshSession, 1000);
    this.userInteractUIHandler = this.userInteractUIHandler.bind(this);
    this.isCustomInsightsBeingUsed = this.isCustomInsightsBeingUsed.bind(this);
    this.handleStorageEvent = this.handleStorageEvent.bind(this);
    this.scheduledTokenRefresh = this.scheduledTokenRefresh.bind(this);
    setInterval(this.isCustomInsightsBeingUsed, 300000);
    this.state = {
      show: false,
      countdown: 60,
      expiryTime: null,
    };

    this.tokenRefreshInFlight = false;
  }

  componentDidMount() {
    window.addEventListener('storage', this.handleStorageEvent);
    if (this.props.isLoggedIn) {
      this.startInactivityTimer();
      this.startActiveSessionTokenRefresh();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.countdown === 0) {
      clearInterval(this.counter);
      window.sessionStorage.clear();
      this.goToLogoutPage();
    }
    const activeCatiSession = window.localStorage.getItem('activeCatiSession');
    if (!this.state.show && this.props.userDetail && !JSON.parse(activeCatiSession) &&
      (this.props.lastTimeUpdatedUI !== prevProps.lastTimeUpdatedUI)) {
      this.resetTimer();
    }
    if (!prevProps.isLoggedIn && this.props.isLoggedIn) {
      this.startInactivityTimer();
      this.startActiveSessionTokenRefresh();
    }
    if (prevProps.isLoggedIn && !this.props.isLoggedIn) {
      this.stopActiveSessionTokenRefresh();
    }
    if (this.props.isLoggedIn && this.props.userDetail && !prevProps.userDetail) {
      this.startActiveSessionTokenRefresh();
    }

    // Ensure we (re)start the refresh interval when the token expiry value arrives/changes.
    const prevExpiresIn = prevProps.userDetail && prevProps.userDetail.expires_in;
    const nextExpiresIn = this.props.userDetail && this.props.userDetail.expires_in;
    if (this.props.isLoggedIn
      && this.props.userDetail
      && prevExpiresIn !== nextExpiresIn) {
      this.startActiveSessionTokenRefresh();
    }
  }

  componentWillUnmount() {
    clearInterval(this.counter);
    clearTimeout(this.timeoutId);
    this.stopActiveSessionTokenRefresh();
    window.removeEventListener('storage', this.handleStorageEvent);
  }

  getExpiresInSeconds = () => {
    const expiresIn = this.props.userDetail && this.props.userDetail.expires_in;
    const expiresInNum = Number(expiresIn);
    if (expiresInNum && !Number.isNaN(expiresInNum) && expiresInNum > 0) {
      return expiresInNum;
    }

    const exp = window.keycloakAuth
      && window.keycloakAuth.tokenParsed
      && window.keycloakAuth.tokenParsed.exp;
    if (typeof exp !== 'number') {
      return null;
    }
    const remaining = exp - Math.floor(Date.now() / 1000);
    return remaining > 0 ? remaining : null;
  };

  getTokenRefreshIntervalMs = () => {
    const expiresInSec = this.getExpiresInSeconds();
    if (!expiresInSec) {
      return null;
    }
    const intervalSec = expiresInSec - (2 * 60);
    return intervalSec > 0 ? intervalSec * 1000 : 0;
  };

  startActiveSessionTokenRefresh = () => {
    this.stopActiveSessionTokenRefresh();
    if (!this.props.isLoggedIn || !this.props.userDetail) {
      return;
    }
    const intervalMs = this.getTokenRefreshIntervalMs();
    if (intervalMs == null) {
      return;
    }
    if (intervalMs <= 0) {
      this.scheduledTokenRefresh();
      return;
    }
    this.tokenRefreshIntervalId = setInterval(this.scheduledTokenRefresh, intervalMs);
  };

  stopActiveSessionTokenRefresh = () => {
    if (this.tokenRefreshIntervalId) {
      clearInterval(this.tokenRefreshIntervalId);
      this.tokenRefreshIntervalId = undefined;
    }
  };

  rescheduleTokenRefreshAfterSync = () => {
    if (!this.props.isLoggedIn || !this.props.userDetail) {
      return;
    }
    const intervalMs = this.getTokenRefreshIntervalMs();
    if (intervalMs == null || intervalMs <= 0) {
      return;
    }
    this.stopActiveSessionTokenRefresh();
    this.tokenRefreshIntervalId = setInterval(this.scheduledTokenRefresh, intervalMs);
  };

  scheduledTokenRefresh = () => {
    if (!this.props.isLoggedIn || !this.props.userDetail) {
      return;
    }
    this.refreshKeycloakTokenAndSync(-1, true)
      .catch(() => {})
      .finally(() => {
        this.rescheduleTokenRefreshAfterSync();
      });
  };
  endCati = type => dispatchEndCatiSession({
    userDetail: this.props.userDetail,
    currentActiveProgram: this.props.currentActiveProgram,
    endCATIActiveSessions: this.props.actions.endCATIActiveSessions,
    endCATIProcess: this.props.actions.endCATIProcess,
    type,
  });

  handleStorageEvent(event) {
    const lastTimeUserIntereacted = window.localStorage.getItem('lastTimeUserIntereacted');
    if (!event || event.key !== 'lastTimeUserIntereacted') return;
    const newValue = event.newValue;
    if (newValue === lastTimeUserIntereacted) {
      this.resetTimer();
    }
  }

  startInactivityTimer = () => {
    this.inactivityTimeout = this.timer();
  };

  clearInactivityTimer = () => {
    clearTimeout(this.inactivityTimeout);
  };

  /**
   * User interact UI handler
   */
  userInteractUIHandler() {
    this.clearInactivityTimer();
    const currentTime = (new Date()).getTime();
    if ((currentTime - this.props.lastTimeUpdatedUI) > 1000) {
      this.props.actions.setLastTimeInteractUI(currentTime);
      window.localStorage.setItem('lastTimeUserIntereacted', currentTime);
    }
  }

  refreshKeycloakTokenAndSync = async (minValidity, syncToBackend = false) => {
    if (!window.keycloakAuth || !window.keycloakAuth.updateToken) {
      return false;
    }
    if (this.tokenRefreshInFlight) {
      return false;
    }

    this.tokenRefreshInFlight = true;
    try {
      ensurePromiseWithResolvers();
      const refreshed = await window.keycloakAuth.updateToken(minValidity);
      const keycloakTokens = {
        accessToken: window.keycloakAuth.token,
        idToken: window.keycloakAuth.idToken,
        refreshToken: window.keycloakAuth.refreshToken,
      };
      const currentJwt = this.props.userDetail && this.props.userDetail.jwtToken;
      if (refreshed
        && keycloakTokens.accessToken
        && keycloakTokens.accessToken !== currentJwt) {
        this.props.actions.updateJwtToken(keycloakTokens.accessToken);
      }
      if (syncToBackend) {
        await this.props.actions.updateTokenOnServer(keycloakTokens);
      }
      return refreshed;
    } finally {
      this.tokenRefreshInFlight = false;
    }
  };

  /**
   * Call Ping api to update the session if user is active inside Custom Insights (DX)
   */
  isCustomInsightsBeingUsed() {
    const userActive = window.localStorage.getItem('isUserActiveInDx');
    if (userActive && JSON.parse(userActive)) {
      this.props.actions.pingServer();
      window.localStorage.setItem('isUserActiveInDx', false);
    }
  }

  /**
   * Refresh session
   */
  refreshSession() {
    const currentTime = (new Date()).getTime();
    const {
      lastTimePingServer, lastTimeUpdatedUI, lastTimeCatiSessionChecked,
      requesting, catiRequesting,
    } = this.props;
    const activeCatiSession = window.localStorage.getItem('activeCatiSession');
    if (JSON.parse(activeCatiSession)) {
      clearTimeout(this.timeoutId);
      /* Gated on its own timestamp and its own in-flight flag rather than the ones
         PING_SERVER_* uses - see lastTimeCatiSessionChecked's/catiRequesting's reducer
         handlers for why. */
      if (!catiRequesting && this.props.userDetail
        && (!lastTimeCatiSessionChecked || (currentTime - lastTimeCatiSessionChecked) > 60000)) {
        this.props.actions.getCatiSession();
      }
    } else if (!requesting && this.props.userDetail && lastTimePingServer && !this.state.show
        && ((currentTime - lastTimePingServer) > 60000)
      && (lastTimePingServer < lastTimeUpdatedUI)) {
      this.props.actions.pingServer();
    }
  }

  timer = () => setTimeout(() => {
    this.showSessionModal();
  }, ((this.props.userDetail.sessionTimeOutValue -
    60) * 1000));

  showSessionModal = () => {
    const expiryTime = Date.now() + (60 * 1000);
    this.setState({ show: true, expiryTime });

    if (!this.counter) {
      this.counter = setInterval(this.updateCountDown, 1000);
    }
  };

  updateCountDown = () => {
    if (!this.state.show || !this.state.expiryTime) return;
    const remaining = Math.max(0, Math.round((this.state.expiryTime - Date.now()) / 1000));
    this.setState({ countdown: remaining });
  };

  resetTimer = () => {
    this.updateSessionTimeoutState();
    clearTimeout(this.timeoutId);
    if (this.counter) {
      clearInterval(this.counter);
      this.counter = undefined;
    }
    this.timeoutId = this.timer();
  };

  resetSession = async () => {
    this.resetTimer();

    try {
      await this.refreshKeycloakTokenAndSync(-1, true);
    } catch (err) {
      console.error('Token refresh failed during session extend:', err);
      return;
    }

    const res = await this.props.actions.pingServer();
    if (res && res.message === 'pong') {
      this.props.alertActions.showSuccessNotificationToast(
        Constants.MESSAGE_BOX_ID.SESSION_EXTENDED,
        SESSION_EXTENDED_SUCCESS_MESSAGE,
      );
    }
  };

  updateSessionTimeoutState = () => {
    this.setState({
      show: false,
      countdown: 60,
      expiryTime: null,
    });
  };

  goToLogoutPage = () => {
    this.setState({
      show: false,
      countdown: -1,
      expiryTime: null,
    });
    this.endCati('end');
    setTimeout(() => this.props.history.push('/logout'), 3000);
  }

  renderModalBody = () => (
    <Fragment>
      <div>Your session is about to expire. You will be logged out in:</div>
      <div className="count-down">{this.state.countdown} seconds</div>
      <div>Please select “Extend Session” to stay logged in, or “Log Out” to log out.</div>
    </Fragment>
  );

  renderModalFooter = () => (
    <Fragment>
      <ButtonV2
        muiButtonProps={{
          'aria-label': 'Confirm',
          variant: 'contained',
          onClick: () => this.resetSession(),
        }}
      >
        Extend Session
      </ButtonV2>
      <ButtonV2
        muiButtonProps={{
          'aria-label': 'Cancel',
          variant: 'outlined',
          color: 'secondary',
          onClick: () => this.goToLogoutPage(),
          sx: { ml: 2 },
        }}
      >
        Log Out
      </ButtonV2>
    </Fragment>
  );

  /**
   * Render
   */
  render() {
    return (
      <Fragment>
        <EventListener
          target="window"
          onMouseMoveCapture={this.userInteractUIHandler}
          onMouseUp={this.userInteractUIHandler}
          onMouseDown={this.userInteractUIHandler}
          onKeyUp={this.userInteractUIHandler}
          onKeyDown={this.userInteractUIHandler}
          onWheel={this.userInteractUIHandler}
        />
        <InformationModal
          id="warningModal"
          aria-modal="true"
          alignMessage=""
          className={styles['session-expiry-modal']}
          backdrop="static"
          title="Session Timeout"
          body={this.renderModalBody()}
          onHide={this.goToLogoutPage}
          show={this.state.show}
          footer={this.renderModalFooter()}
          footerAlign="right"
        />
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  lastTimePingServer: state.SessionRefresherReducer.get('lastTimePingServer'),
  lastTimeCatiSessionChecked: state.SessionRefresherReducer.get('lastTimeCatiSessionChecked'),
  lastTimeUpdatedUI: state.SessionRefresherReducer.get('lastTimeUpdatedUI'),
  requesting: state.SessionRefresherReducer.get('requesting'),
  catiRequesting: state.SessionRefresherReducer.get('catiRequesting'),
  userDetail: state.UserDetail.get('userDetail'),
  isLoggedIn: state.UserDetail.get('isLoggedIn'),
  currentActiveProgram: state.UserDetail.get('currentActiveProgram'),
});

const mapDispatchToProps = dispatch => ({
  actions:
    bindActionCreators(
      Object.assign({}, actions, { updateJwtToken }),
      dispatch,
    ),
  alertActions: bindActionCreators(
    Object.assign({}, MessageBoxActions),
    dispatch,
  ),
});

SessionRefresher.propTypes = {
  lastTimePingServer: PropTypes.number,
  lastTimeCatiSessionChecked: PropTypes.number,
  lastTimeUpdatedUI: PropTypes.number,
  requesting: PropTypes.bool,
  catiRequesting: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  actions: PropTypes.shape({
    setLastTimeInteractUI: PropTypes.func,
    pingServer: PropTypes.func,
    getCatiSession: PropTypes.func,
    updateTokenOnServer: PropTypes.func,
    updateJwtToken: PropTypes.func.isRequired,
    endCATIActiveSessions: PropTypes.func,
    endCATIProcess: PropTypes.func,
  }).isRequired,
  userDetail: PropTypes.shape({
    sessionTimeOutValue: PropTypes.number,
    username: PropTypes.string,
    jwtToken: PropTypes.string,
    expires_in: PropTypes.number,
    keycloakUrl: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  alertActions: PropTypes.shape({
    showSuccessNotificationToast: PropTypes.func.isRequired,
  }).isRequired,
  currentActiveProgram: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.shape()),
    permissions: PropTypes.arrayOf(PropTypes.shape()),
    displayName: PropTypes.string,
    programId: PropTypes.number,
    alternateText: PropTypes.string,
    programLogo: PropTypes.string,
    supportedLanguages: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      lng: PropTypes.string,
      locale: PropTypes.string,
    }).isRequired),
    programCode: PropTypes.string,
  }),
};

SessionRefresher.defaultProps = {
  lastTimePingServer: null,
  lastTimeCatiSessionChecked: null,
  lastTimeUpdatedUI: null,
  requesting: false,
  catiRequesting: false,
  isLoggedIn: false,
  userDetail: null,
  currentActiveProgram: null,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SessionRefresher);

export const SessionRefresherComponent = SessionRefresher;
